import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { updateContractRegistrationDate } from '@/modules/Contract/services/customer'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { formatPGDate } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { schemaRegistrationDateModal } from './schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface RegistrationDateFormValues {
    registration_date: string
}

export interface IRegistrationDateModalProps extends ModalProps {
    contractID: string
    attachment?: Attachment
    actionType?: 'preview' | 'download'
    onSubmitSuccess?: () => void
}

const RegistrationDateModal = ({
    onClose,
    open,
    contractID,
    attachment,
    actionType = 'preview',
    onSubmitSuccess,
}: IRegistrationDateModalProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'contracts',
        mode: 'contains',
    })
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    const formControl = useForm<RegistrationDateFormValues>({
        defaultValues: {
            registration_date: new Date().toISOString().split('T')[0],
        },
        resolver: yupResolver(schemaRegistrationDateModal as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateContractRegistrationDate,
        },
        {
            successMessage: t('update-registration-date-success'),
            reactQueryKeys: QUERY_KEYS.CONTRACTS,
            onSuccess: async () => {
                onClose()
                refetch()
                onSubmitSuccess?.()

                // Perform the appropriate action after successful registration date submission
                if (attachment) {
                    if (actionType === 'preview') {
                        await openFilePreview(attachment)
                    } else if (actionType === 'download') {
                        await downloadFileWithPresigned(attachment)
                    }
                }
            },
        }
    )

    const onSubmit = async (values: RegistrationDateFormValues) => {
        await mutateAsync({
            contractID: Number(contractID),
            data: {
                registration_date: formatPGDate(
                    values.registration_date,
                    'YYYY-MM-DD'
                ),
            },
        })
    }

    return (
        <Dialog
            title={t('update_registration_date')}
            onClose={onClose}
            open={open}
        >
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Typogaphy className="text-primary font-medium text-sm">
                    📅 {t('registration_date')}
                </Typogaphy>
                <Typogaphy className="text-primary text-sm mt-2">
                    {t('registration_date_description')}
                </Typogaphy>
            </div>

            <GridHookForm
                className="!gap-2"
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('update_registration_date'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        formItemProps: {
                            name: 'registration_date',
                            type: 'datepicker',
                            label: t('registration_date'),
                            placeholder: t('registration_date_placeholder'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default RegistrationDateModal
